import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Trash2, AlertCircle, Bell, BellOff } from 'lucide-react';
import MySelectionShare from './MySelectionShare';

const STATE_STYLE = {
  pending: 'bg-white/10 text-white border-white/30',
  live: 'bg-yellow-400/15 text-yellow-300 border-yellow-400/50',
  won: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/60',
  lost: 'bg-red-500/20 text-red-300 border-red-500/60',
  void: 'bg-white/5 text-gray-400 border-white/15',
};
const LIVE = new Set(['1H', 'HT', '2H', 'ET', 'BT', 'P', 'LIVE', 'INT', 'SUSP']);

const marketStyle = (p) => {
  if (p.state === 'unverifiable') return LIVE.has(p.status) ? STATE_STYLE.live : STATE_STYLE.pending;
  return STATE_STYLE[p.state] || STATE_STYLE.pending;
};

const timeLabel = (p) => {
  if (LIVE.has(p.status)) return p.status === 'HT' ? 'INT' : `${p.elapsed ?? ''}'`;
  if (['FT', 'AET', 'PEN'].includes(p.status)) return 'FIN';
  if (['PST', 'CANC', 'ABD'].includes(p.status)) return 'RINV';
  const parts = (p.match_date || '').split(/[-/]/);
  const d = parts.length === 3 ? (parts[0].length === 4 ? `${parts[2]}/${parts[1]}` : `${parts[0]}/${parts[1]}`) : '';
  return `${d} ${p.match_time || ''}`.trim();
};

const Row = ({ p, onOpen, onRemove }) => {
  const started = p.status && p.status !== 'NS' && p.status !== 'TBD';
  return (
    <div className="flex items-stretch bg-[#1E1E1E] border-b border-white/5" data-testid={`my-selection-row-${p.fixture_id}`}>
    <button onClick={onOpen} className="flex-1 min-w-0 flex items-center gap-3 p-3 text-left active:bg-[#2A2A2A]" data-testid={`my-selection-open-${p.fixture_id}`}>
      <span className={`w-12 text-[10px] font-bold text-center flex-shrink-0 ${LIVE.has(p.status) ? 'text-red-400' : 'text-gray-500'}`}>{timeLabel(p)}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm text-white truncate">{p.home_team}</span>
          {started && <span className="text-sm font-bold text-white">{p.score_home ?? 0}</span>}
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm text-white truncate">{p.away_team}</span>
          {started && <span className="text-sm font-bold text-white">{p.score_away ?? 0}</span>}
        </div>
        <div className="mt-1.5 flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded-md border text-[11px] font-black uppercase ${marketStyle(p)}`} data-testid={`my-selection-market-${p.fixture_id}`} data-state={p.state}>
            {p.market || 'Mercato'}
          </span>
          {p.state === 'unverifiable' && <span className="text-[10px] text-gray-500">da verificare</span>}
        </div>
      </div>
    </button>
    <button
      onClick={onRemove}
      className="px-3 flex items-center text-gray-500 hover:text-red-400 active:scale-90 transition-transform"
      aria-label="Rimuovi partita"
      data-testid={`my-selection-remove-${p.fixture_id}`}
    >
      <Trash2 className="w-4 h-4" />
    </button>
    </div>
  );
};

export const MySelectionList = ({ selection, importing, onFile, onRemove, onRemovePick, onToggleNotify }) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const picks = selection?.picks || [];
  const counts = picks.reduce((a, p) => ({ ...a, [p.state]: (a[p.state] || 0) + 1 }), {});
  return (
    <div data-testid="my-selection-list">
      <div className="flex items-center justify-between mb-2 text-xs">
        <span className="text-gray-400" data-testid="my-selection-summary">
          {picks.length} partite · <span className="text-emerald-400">{counts.won || 0} ✓</span> · <span className="text-red-400">{counts.lost || 0} ✗</span>
        </span>
        <div className="flex items-center gap-3">
          <button onClick={onToggleNotify} className={`flex items-center ${selection?.notify ? 'text-[#F5A623]' : 'text-gray-500'}`} aria-label="Avvisi selezione" data-testid="my-selection-notify-toggle" data-active={selection?.notify ? 'true' : 'false'}>
            {selection?.notify ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
          </button>
          <button onClick={() => inputRef.current?.click()} disabled={importing} className="flex items-center gap-1 text-[#F5A623] font-bold" data-testid="my-selection-change-btn">
            <RefreshCw className={`w-3 h-3 ${importing ? 'animate-spin' : ''}`} /> Cambia selezione
          </button>
          <button onClick={onRemove} className="flex items-center gap-1 text-gray-500" data-testid="my-selection-delete-btn">
            <Trash2 className="w-3 h-3" /> Elimina
          </button>
        </div>
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; onFile(f); }} data-testid="my-selection-change-input" />
      </div>
      <div className="rounded-xl overflow-hidden">
        {picks.map((p) => <Row key={p.fixture_id} p={p} onOpen={() => navigate(`/match/${p.fixture_id}`)} onRemove={() => onRemovePick(p.fixture_id)} />)}
      </div>
      {picks.length > 0 && <MySelectionShare picks={picks} />}
      {selection?.unrecognized?.length > 0 && (
        <p className="mt-3 text-[11px] text-gray-500 flex items-start gap-1.5" data-testid="my-selection-unrecognized">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>
            Non riconosciute: {selection.unrecognized.join(', ')}
            <span className="block text-gray-600 mt-0.5" data-testid="my-selection-unrecognized-hint">Cercale con la lente e tocca la stellina per aggiungerle.</span>
          </span>
        </p>
      )}
    </div>
  );
};

export default MySelectionList;
